import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { DynamicRedirectAction, ScoreRedirect } from '@/types/quiz';
import { Plus, Trash2 } from 'lucide-react';

const redirectSchema = z.object({
  id: z.string(),
  minScore: z.coerce.number().min(0).max(100),
  maxScore: z.coerce.number().min(0).max(100),
  url: z.string().url('Please enter a valid URL'),
  openInNewTab: z.boolean(),
}).refine((data) => data.minScore <= data.maxScore, {
  message: 'Min score must be less than or equal to max score',
  path: ['minScore'],
});

const schema = z.object({
  name: z.string().min(1, 'Action name is required'),
  redirects: z.array(redirectSchema).min(1, 'At least one redirect is required'),
});

type FormData = z.infer<typeof schema>;

interface DynamicRedirectActionFormProps {
  action?: DynamicRedirectAction;
  onSave: (action: Omit<DynamicRedirectAction, 'id'> & { id?: string }) => void;
  onCancel: () => void;
}

export function DynamicRedirectActionForm({ action, onSave, onCancel }: DynamicRedirectActionFormProps) {
  const { register, handleSubmit, formState: { errors }, control, watch } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: action?.name || '',
      redirects: action?.redirects || [{
        id: crypto.randomUUID(),
        minScore: 0,
        maxScore: 50,
        url: '',
        openInNewTab: false,
      }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'redirects',
  });

  const onSubmit = (data: FormData) => {
    onSave({
      id: action?.id,
      type: 'dynamic-redirect',
      name: data.name,
      redirects: data.redirects as ScoreRedirect[],
    });
  };

  const addRedirect = () => {
    append({
      id: crypto.randomUUID(),
      minScore: 0,
      maxScore: 100,
      url: '',
      openInNewTab: false,
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(onSubmit)(e);
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Action Name</Label>
        <Input
          id="name"
          placeholder="e.g., Score-based redirects"
          {...register('name')}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Score-Based Redirects</Label>
          <Button type="button" size="sm" variant="outline" onClick={addRedirect}>
            <Plus className="h-4 w-4 mr-1" />
            Add Redirect
          </Button>
        </div>

        {fields.map((field, index) => (
          <div key={field.id} className="border rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-start">
              <h4 className="text-sm font-medium">Redirect {index + 1}</h4>
              {fields.length > 1 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor={`redirects.${index}.minScore`}>Min Score (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  {...register(`redirects.${index}.minScore`)}
                />
                {errors.redirects?.[index]?.minScore && (
                  <p className="text-sm text-destructive">
                    {errors.redirects[index]?.minScore?.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor={`redirects.${index}.maxScore`}>Max Score (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  {...register(`redirects.${index}.maxScore`)}
                />
                {errors.redirects?.[index]?.maxScore && (
                  <p className="text-sm text-destructive">
                    {errors.redirects[index]?.maxScore?.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`redirects.${index}.url`}>Redirect URL</Label>
              <Input
                type="url"
                placeholder="https://example.com/result"
                {...register(`redirects.${index}.url`)}
              />
              {errors.redirects?.[index]?.url && (
                <p className="text-sm text-destructive">
                  {errors.redirects[index]?.url?.message}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id={`redirects.${index}.openInNewTab`}
                checked={watch(`redirects.${index}.openInNewTab`)}
                onCheckedChange={(checked) => {
                  const currentRedirects = watch('redirects');
                  currentRedirects[index].openInNewTab = checked as boolean;
                }}
              />
              <Label htmlFor={`redirects.${index}.openInNewTab`} className="cursor-pointer">
                Open in new tab
              </Label>
            </div>
          </div>
        ))}

        {errors.redirects && typeof errors.redirects.message === 'string' && (
          <p className="text-sm text-destructive">{errors.redirects.message}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {action ? 'Update Action' : 'Add Action'}
        </Button>
      </div>
    </form>
  );
}
